import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import { doc, onSnapshot } from "firebase/firestore";

import VideoPlayer from "../components/Archive/VideoPlayer";
import ChatContainer from "../components/Archive/ChatContainer";
import SummaryContainer from "../components/Archive/SummaryContainer";
import useVideoData from "../hooks/useVideoData";
import useAISummary from "../hooks/useAISummary";

const ArchivePage = () => {
  // ==================== STATE ====================
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [intervals, setIntervals] = useState([]);
  const [currentInterval, setCurrentInterval] = useState(null);
  const [summaryFormat, setSummaryFormat] = useState("bullet");
  const [showSummary, setShowSummary] = useState(false);
  const [currentVideoTime, setCurrentVideoTime] = useState(0); // Add current time tracking
  const [overlayTextReal, setOverlayTextReal] = useState("");

  // ==================== DATA HOOKS ====================
  const {
    metadata,
    chatData,
    displayedMessages,
    allShownMessages,
    isLoading,
    error,
    setAllShownMessages,
    setDisplayedMessages,
  } = useVideoData(selectedVideoId);

  // AI Summary hook with current time
  const { overlayText, topics, languages } = useAISummary(
    allShownMessages,
    metadata,
    currentVideoTime, // Pass current time for time-based updates
  );
  const shouldShowAIButton = summaryFormat !== "none";

  useEffect(() => {
    /**
     * NOTE: look into use memo
     * if this overlayText is different to overlayTextReal then, update our overlaytextreal to our new overlaytext from api
     * setOverlayTextReal(overlayText);
     *
     */
    if (overlayText !== overlayTextReal) {
      setOverlayTextReal(overlayText);
    }
  }, [overlayText, overlayTextReal]);
  //SUMMRAY REFACTOR useEffect will have dependency of overlayText

  // ==================== DEBUG LOGGING ====================
  useEffect(() => {
    console.log("ArchivePage AI data updated:", {
      overlayTextLength: overlayText ? overlayText.length : 0,
      topicsCount: topics ? topics.length : 0,
      languagesCount: languages ? languages.length : 0,
      summaryFormat,
      showSummary,
      allShownMessagesCount: allShownMessages ? allShownMessages.length : 0,
      currentVideoTime,
    });
  }, [
    overlayText,
    topics,
    languages,
    summaryFormat,
    showSummary,
    allShownMessages,
    currentVideoTime,
  ]);

  // ==================== HANDLERS ====================
  const handleIntervalChange = (intervalIndex, interval) => {
    console.log("=== INTERVAL CHANGE ===");
    console.log("Previous interval:", currentInterval);
    console.log("New interval:", {
      intervalIndex,
      interval,
      summaryType: interval.summaryType,
    });

    setCurrentInterval(interval);

    const summaryType = interval.summaryType;
    console.log("Summary type:", summaryType);

    if (summaryType === "P") {
      console.log("Setting paragraph format");
      setSummaryFormat("paragraph");
      setShowSummary(true);
    } else if (summaryType === "B") {
      console.log("Setting bullet format");
      setSummaryFormat("bullet");
      setShowSummary(true);
    } else if (summaryType === "N") {
      console.log("Setting no summary");
      setSummaryFormat("none");
      setShowSummary(false);
    } else {
      console.log("Unknown summary type, defaulting to bullet");
      setSummaryFormat("bullet");
      setShowSummary(true);
    }

    console.log("=== END INTERVAL CHANGE ===");
  };

  // Handle video time updates from VideoPlayer
  const handleTimeUpdate = (time) => {
    setCurrentVideoTime(time);
  };

  // ==================== FIRESTORE LISTENER ====================
  useEffect(() => {
    const teamVideoRef = doc(db, "teamSync", "currentVideo");
    const unsubscribe = onSnapshot(teamVideoRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log("TeamSync data:", data);

        if (data.videoId && data.videoId !== selectedVideoId) {
          setSelectedVideoId(data.videoId);
          setCurrentVideoTime(0); // Reset time when video changes
        }

        if (data.time_ranges) {
          console.log("Time ranges from teamSync:", data.time_ranges);
          setIntervals(data.time_ranges);
        } else {
          console.log("No time_ranges found in teamSync");
          setIntervals([]);
        }
      } else {
        console.log("TeamSync document does not exist");
        setSelectedVideoId(null);
        setIntervals([]);
        setCurrentVideoTime(0);
      }
    });

    return () => unsubscribe();
  }, [selectedVideoId]);

  // ==================== RENDER ====================
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-white">
        Loading video data...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen text-red-500">
        {error}
      </div>
    );
  }

  if (!selectedVideoId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-center">
        <div className="text-xl text-white mb-4">No video configured</div>
        <div className="text-gray-400">
          Go to Admin Panel → Archive Settings to configure your videos
        </div>
      </div>
    );
  }

  console.log("=== RENDERING ARCHIVE PAGE ===");
  console.log("Props being passed to ChatContainer and SummaryContainer:", {
    summaryFormat,
    showSummary,
    overlayText: overlayText ? `${overlayText.substring(0, 50)}...` : "null",
    topics: topics?.length || 0,
    languages: languages?.length || 0,
    displayedMessages: displayedMessages?.length || 0,
    currentVideoTime,
  });

  return (
    <div className="min-h-screen app-container flex flex-col xl:pt-20 desktop:pt-10">
      <div className="flex flex-grow justify-around items-start flex-wrap mt-10">
        <VideoPlayer
          metadata={metadata}
          selectedVideoId={selectedVideoId}
          chatData={chatData}
          setAllShownMessages={setAllShownMessages}
          setDisplayedMessages={setDisplayedMessages}
          intervals={intervals}
          onIntervalChange={handleIntervalChange}
          onTimeUpdate={handleTimeUpdate} // Add time update handler
        />

        {/** SummaryContainer -- you're sending it overlayTextReal */}
        <div className="switchable-chat-container">
          <div className="desktop:w-[25vw] desktop:h-[85vh] xl:w-[400px] xl:h-[600px] lg:w-[350px] lg:h-[500px] md:w-[300px] md:h-[450px] w-[280px] h-[400px]">
            <div className="chat-content flex-grow mt-6 h-[90%] md:h-[80%]">
              <div className="w-full h-[500px] bg-neutral relative text-white overflow-hidden rounded-md">
                {/* AI Summary Overlay Panel */}
                {shouldShowAIButton && (
                  <div
                    className={`absolute top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out ${showSummary ? "max-h-64 opacity-100 py-3 px-4" : "max-h-0 opacity-0 py-3 px-4"} overflow-hidden`}
                  >
                    <div className="flex justify-between items-start">
                      <SummaryContainer
                        summaryFormat={summaryFormat}
                        overlayText={overlayTextReal}
                        topics={topics}
                        languages={languages}
                      />
                      <button
                        onClick={() => setShowSummary(false)}
                        className="ml-4 px-3 text-white hover:text-red-400 text-2xl font-bold"
                        title="Close Summary"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}

                {/* Show Summary Button */}
                {/* Manual test button for debugging */}
                {!showSummary && shouldShowAIButton && (
                  <div className="absolute top-2 right-4 z-40 flex gap-2">
                    {/*{updateOverlayText && (
                        <button onClick={() => updateOverlayText("manual-test")} className="text-white text-sm px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded" title="Test AI Update">
                            Test AI
                        </button>
                    )}*/}
                    <button
                      onClick={() => setShowSummary(true)}
                      className="btn btn-sm rounded-full shadow-md hover:shadow-lg transition-all bg-blue-500 hover:bg-blue-600 border-none text-white"
                      title="Show AI Summary"
                    >
                      <span className="hidden md:inline">AI Details</span>
                      <span className="md:hidden">AI</span>
                    </button>
                  </div>
                )}

                {/* Chat Messages Container */}
                <div
                  className={`transition-all duration-500 ease-in-out absolute bottom-0 left-0 right-0 overflow-hidden rounded-t-none rounded-b-lg ${showSummary && shouldShowAIButton ? "h-[270px]" : "h-[270px]"}`}
                >
                  <ChatContainer
                    displayedMessages={displayedMessages}
                    currentTime={currentVideoTime}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchivePage;
